import { cloneDeep } from 'lodash';
import {
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
    FEEDBACK_STRUCTURE,
} from 'utils/constants';
import { isUnscoredRubric, RubricScoringMode } from 'utils/rubricScoring';

export const ROLLOVER_GROUPS = Object.freeze({
    DESCRIPTION: 'description',
    RUBRIC: 'rubric',
    CITATION_FORMAT: 'citationFormat',
    CONTENT_BANK: 'contentBank',
    GRADING: 'grading',
    FEEDBACK: 'feedback',
    FORMATIVE: 'formative',
    INLINE_COMMENTS: 'inlineComments',
});

const ROLLOVER_GROUP_IDS = Object.freeze(Object.values(ROLLOVER_GROUPS));

export const ROLLOVER_UNAVAILABLE_REASONS = Object.freeze({
    ADMIN_DISABLED: 'admin-disabled',
    TEMPLATED_FEEDBACK: 'templated-feedback',
});

const DEFAULT_ENABLEMENT = Object.freeze({
    aiGradingEnabled: true,
    aiFeedbackEnabled: true,
    aiStudentFeedbackEnabled: false,
    aiInlineCommentsEnabled: true,
});

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const mapFeedbackSettings = (settings) => {
    const value = isObject(settings) ? settings : {};
    const structure = value.structure ?? DEFAULT_FEEDBACK_STRUCTURE;
    return {
        structure,
        numSuggestions: value.num_suggestions ?? DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
        suggestionLength: value.suggestion_length ?? DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
        languageLevel: value.language_level ?? DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
        tone: value.tone ?? DEFAULT_FEEDBACK_TONE,
        templated:
            structure === FEEDBACK_STRUCTURE.TEMPLATED
                ? cloneDeep(isObject(value.templated) ? value.templated : {})
                : {},
        customInstructions: value.custom_instructions ?? '',
    };
};

const mapInlineCommentsSettings = (settings) => {
    const value = isObject(settings) ? settings : {};
    return {
        maxCommentsPerPage: value.max_comments_per_page ?? DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
        commentLength: value.comment_length ?? DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
        languageLevel: value.language_level ?? DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
        tone: value.tone ?? DEFAULT_INLINE_COMMENTS_TONE,
    };
};

/**
 * Converts persisted assignment fields into the field names used by the create/edit form.
 * Identity, title, LMS linkage, publication state, and generated_id are intentionally excluded.
 */
export const assignmentRolloverFromApi = (assignment = {}) => ({
    description: assignment.description ?? '',
    rubric: assignment.rubric
        ? {
              ...cloneDeep(assignment.rubric),
              ...(isUnscoredRubric(assignment) ? { scoring_mode: RubricScoringMode.UNSCORED } : {}),
          }
        : null,
    citationFormat: assignment.citation_format ?? CITATION_SELECT_OPTIONS[0].key,
    contentBankResources: cloneDeep(
        Array.isArray(assignment.content_bank_resources)
            ? assignment.content_bank_resources.filter((resource) => resource && resource.active !== false)
            : []
    ),
    aiGradingEnabled: assignment.ai_grading_enabled ?? DEFAULT_ENABLEMENT.aiGradingEnabled,
    gradingSettings: cloneDeep(isObject(assignment.ai_grading_settings) ? assignment.ai_grading_settings : null),
    gradingCustomContext: assignment.ai_grading_context ?? '',
    aiFeedbackEnabled: assignment.ai_feedback_enabled ?? DEFAULT_ENABLEMENT.aiFeedbackEnabled,
    feedbackSettings: mapFeedbackSettings(assignment.ai_feedback_settings),
    feedbackCustomContext: assignment.ai_feedback_context ?? '',
    aiStudentFeedbackEnabled: assignment.ai_student_feedback_enabled ?? DEFAULT_ENABLEMENT.aiStudentFeedbackEnabled,
    studentFeedbackSettings: mapFeedbackSettings(assignment.ai_student_feedback_settings),
    studentFeedbackCustomContext: assignment.ai_student_feedback_context ?? '',
    aiInlineCommentsEnabled: assignment.ai_inline_comments_enabled ?? DEFAULT_ENABLEMENT.aiInlineCommentsEnabled,
    inlineCommentsSettings: mapInlineCommentsSettings(assignment.ai_inline_comments_settings),
    inlineCommentsCustomContext: assignment.ai_inline_comments_context ?? '',
});

const normalizeGroupIds = (groups, fieldName) => {
    if (!Array.isArray(groups) && !(groups instanceof Set)) {
        throw new TypeError(`${fieldName} must be an array or Set.`);
    }

    const result = new Set(groups);
    const invalidGroups = [...result].filter((group) => !ROLLOVER_GROUP_IDS.includes(group));
    if (invalidGroups.length) {
        throw new RangeError(`Unknown assignment rollover group(s): ${invalidGroups.join(', ')}.`);
    }
    return result;
};

/**
 * Returns an object keyed by unavailable group id. Values are stable reason strings suitable
 * for deciding which checkboxes to disable without coupling this utility to translated UI copy.
 */
export const getUnavailableRolloverGroups = (sourceAssignment = {}, { disabledGroups = [] } = {}) => {
    const unavailable = {};
    const adminDisabled = normalizeGroupIds(disabledGroups, 'disabledGroups');

    adminDisabled.forEach((group) => {
        unavailable[group] = ROLLOVER_UNAVAILABLE_REASONS.ADMIN_DISABLED;
    });

    if (sourceAssignment.ai_feedback_settings?.structure === FEEDBACK_STRUCTURE.TEMPLATED) {
        unavailable[ROLLOVER_GROUPS.FEEDBACK] = ROLLOVER_UNAVAILABLE_REASONS.TEMPLATED_FEEDBACK;
    }
    if (sourceAssignment.ai_student_feedback_settings?.structure === FEEDBACK_STRUCTURE.TEMPLATED) {
        unavailable[ROLLOVER_GROUPS.FORMATIVE] = ROLLOVER_UNAVAILABLE_REASONS.TEMPLATED_FEEDBACK;
    }

    return unavailable;
};

const getRubricCriteria = (rubric) => (Array.isArray(rubric?.criteria) ? rubric.criteria : []);

/**
 * Criterion-specific grading instructions are keyed by criterion UUID. Since unrelated rubrics
 * do not share UUIDs, rollover pairs criteria by display order and records anything that cannot
 * be paired in metadata rather than leaking stale source UUIDs into the target assignment.
 */
export const remapGradingSettingsByCriterionOrder = ({
    sourceSettings,
    sourceRubric,
    targetRubric,
    gradingMapping,
}) => {
    if (gradingMapping !== undefined && !isObject(gradingMapping)) {
        throw new TypeError('gradingMapping must be an object when provided.');
    }

    if (!isObject(sourceSettings)) {
        return {
            gradingSettings: null,
            metadata: {
                mappings: [],
                unmappedSourceEntries: [],
                targetCriteriaWithoutSource: getRubricCriteria(targetRubric).map(({ id }, index) => ({ id, index })),
            },
        };
    }

    const sourceCriteria = getRubricCriteria(sourceRubric);
    const targetCriteria = getRubricCriteria(targetRubric);
    const sourceEntries = isObject(sourceSettings.criteria) ? sourceSettings.criteria : {};
    const mappedCriteria = {};
    const mappings = [];
    const unmappedSourceEntries = [];
    const consumedSourceIds = new Set();
    const mappedTargetIds = new Set();
    const useManualMapping = gradingMapping !== undefined;

    sourceCriteria.forEach((sourceCriterion, index) => {
        const normalizedSourceCriterionId = `${sourceCriterion.id}`;
        const requestedTargetId = useManualMapping ? gradingMapping[sourceCriterion.id] : targetCriteria[index]?.id;
        const normalizedTargetId = typeof requestedTargetId === 'string' ? requestedTargetId.trim() : requestedTargetId;
        const hasRequestedTarget =
            normalizedTargetId !== undefined && normalizedTargetId !== null && normalizedTargetId !== '';
        const requestedTargetCriterion = hasRequestedTarget
            ? targetCriteria.find(({ id }) => `${id}` === `${normalizedTargetId}`)
            : undefined;
        const requestedTargetCriterionId = requestedTargetCriterion ? `${requestedTargetCriterion.id}` : null;
        const hasSettings = Object.prototype.hasOwnProperty.call(sourceEntries, sourceCriterion.id);
        if (hasSettings && requestedTargetCriterion && mappedTargetIds.has(requestedTargetCriterionId)) {
            throw new RangeError(`Target rubric criterion ${requestedTargetCriterionId} is mapped more than once.`);
        }
        const targetCriterion = requestedTargetCriterion;
        if (hasSettings) consumedSourceIds.add(normalizedSourceCriterionId);

        if (!targetCriterion) {
            if (hasSettings) {
                unmappedSourceEntries.push({
                    sourceCriterionId: sourceCriterion.id,
                    sourceIndex: index,
                    settings: cloneDeep(sourceEntries[sourceCriterion.id]),
                });
            }
            return;
        }

        mappings.push({
            sourceCriterionId: sourceCriterion.id,
            targetCriterionId: targetCriterion.id,
            index,
            hasSettings,
        });
        if (hasSettings) {
            mappedTargetIds.add(`${targetCriterion.id}`);
            mappedCriteria[targetCriterion.id] = cloneDeep(sourceEntries[sourceCriterion.id]);
        }
    });

    Object.entries(sourceEntries).forEach(([sourceCriterionId, settings]) => {
        if (consumedSourceIds.has(sourceCriterionId)) return;

        const requestedTargetId = useManualMapping ? gradingMapping[sourceCriterionId] : undefined;
        const normalizedTargetId = typeof requestedTargetId === 'string' ? requestedTargetId.trim() : requestedTargetId;
        const hasRequestedTarget =
            normalizedTargetId !== undefined && normalizedTargetId !== null && normalizedTargetId !== '';
        const targetCriterion = hasRequestedTarget
            ? targetCriteria.find(({ id }) => `${id}` === `${normalizedTargetId}`)
            : undefined;
        if (targetCriterion) {
            const normalizedTargetCriterionId = `${targetCriterion.id}`;
            if (mappedTargetIds.has(normalizedTargetCriterionId)) {
                throw new RangeError(
                    `Target rubric criterion ${normalizedTargetCriterionId} is mapped more than once.`
                );
            }
            mappedCriteria[targetCriterion.id] = cloneDeep(settings);
            mappings.push({
                sourceCriterionId,
                targetCriterionId: targetCriterion.id,
                index: null,
                hasSettings: true,
            });
            mappedTargetIds.add(normalizedTargetCriterionId);
            return;
        }
        unmappedSourceEntries.push({ sourceCriterionId, sourceIndex: null, settings: cloneDeep(settings) });
    });

    return {
        gradingSettings: {
            ...cloneDeep(sourceSettings),
            criteria: mappedCriteria,
        },
        metadata: {
            mappings,
            unmappedSourceEntries,
            targetCriteriaWithoutSource: targetCriteria
                .map(({ id }, index) => ({ id, index }))
                .filter(({ id }) => !mappedTargetIds.has(`${id}`)),
        },
    };
};

const assertGroupsAreAvailable = ({ sourceAssignment, selectedGroups, disabledGroups }) => {
    const unavailable = getUnavailableRolloverGroups(sourceAssignment, { disabledGroups });
    const selectedUnavailable = [...selectedGroups].filter((group) => unavailable[group]);
    if (selectedUnavailable.length) {
        const details = selectedUnavailable.map((group) => `${group} (${unavailable[group]})`).join(', ');
        throw new RangeError(`Unavailable assignment rollover group(s): ${details}.`);
    }
};

export const applyAssignmentRolloverWithMetadata = ({
    targetForm,
    sourceAssignment,
    selectedGroups,
    disabledGroups = [],
    gradingMapping,
}) => {
    const selected = normalizeGroupIds(selectedGroups, 'selectedGroups');
    assertGroupsAreAvailable({ sourceAssignment, selectedGroups: selected, disabledGroups });

    const source = assignmentRolloverFromApi(sourceAssignment);
    const form = cloneDeep(targetForm ?? {});
    const metadata = { grading: null };

    if (selected.has(ROLLOVER_GROUPS.DESCRIPTION)) form.description = source.description;
    if (selected.has(ROLLOVER_GROUPS.RUBRIC)) form.rubric = source.rubric;
    if (selected.has(ROLLOVER_GROUPS.CITATION_FORMAT)) form.citationFormat = source.citationFormat;
    if (selected.has(ROLLOVER_GROUPS.CONTENT_BANK)) {
        form.contentBankResources = source.contentBankResources;
    }

    if (selected.has(ROLLOVER_GROUPS.GRADING)) {
        const effectiveTargetRubric = selected.has(ROLLOVER_GROUPS.RUBRIC) ? source.rubric : form.rubric;
        const remapped = remapGradingSettingsByCriterionOrder({
            sourceSettings: source.gradingSettings,
            sourceRubric: source.rubric,
            targetRubric: effectiveTargetRubric,
            gradingMapping,
        });
        form.aiGradingEnabled = source.aiGradingEnabled;
        form.gradingSettings = remapped.gradingSettings;
        form.gradingCustomContext = source.gradingCustomContext;
        metadata.grading = remapped.metadata;
    }

    if (selected.has(ROLLOVER_GROUPS.FEEDBACK)) {
        form.aiFeedbackEnabled = source.aiFeedbackEnabled;
        form.feedbackSettings = source.feedbackSettings;
        form.feedbackCustomContext = source.feedbackCustomContext;
    }

    if (selected.has(ROLLOVER_GROUPS.FORMATIVE)) {
        form.aiStudentFeedbackEnabled = source.aiStudentFeedbackEnabled;
        form.studentFeedbackSettings = source.studentFeedbackSettings;
        form.studentFeedbackCustomContext = source.studentFeedbackCustomContext;
    }

    if (selected.has(ROLLOVER_GROUPS.INLINE_COMMENTS)) {
        form.aiInlineCommentsEnabled = source.aiInlineCommentsEnabled;
        form.inlineCommentsSettings = source.inlineCommentsSettings;
        form.inlineCommentsCustomContext = source.inlineCommentsCustomContext;
    }

    return { form, metadata };
};

/**
 * Applies a one-time snapshot overwrite and returns a new create/edit form object.
 */
export const applyAssignmentRollover = (options) => applyAssignmentRolloverWithMetadata(options).form;
